import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';

export default function SMCCCallback() {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    // Check for success or error in URL params
    const success = searchParams.get('success');
    const error = searchParams.get('error');
    const errorMessage = searchParams.get('message');
    const platform = searchParams.get('platform');
    const accountName = searchParams.get('account_name');

    if (success === 'true') {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Process URL params on mount
      setStatus('success');
      setMessage(
        accountName
          ? `Successfully connected @${accountName} on ${platform}`
          : `Successfully connected ${platform} account`
      );
      // Redirect after a short delay
      setTimeout(() => {
        navigate('/admin/smcc/accounts');
      }, 2000);
    } else if (error) {
      setStatus('error');
      setMessage(errorMessage || error || 'Failed to connect account');
    } else {
      // No params - redirect to accounts
      navigate('/admin/smcc/accounts');
    }
  }, [searchParams, navigate]);

  return (
    <div className="min-h-[400px] flex items-center justify-center">
      <div className="text-center">
        {status === 'loading' && (
          <>
            <Loader2 className="w-12 h-12 text-primary-600 animate-spin mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-gray-900">{t('admin.smcc.connecting', 'Connecting account...')}</h2>
            <p className="text-gray-500 mt-2">Please wait while we complete the connection.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Account Connected!</h2>
            <p className="text-gray-500 mt-2">{message}</p>
            <p className="text-sm text-gray-400 mt-4">Redirecting to accounts page...</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Connection Failed</h2>
            <p className="text-gray-500 mt-2">{message}</p>
            <button
              onClick={() => navigate('/admin/smcc/accounts')}
              className="mt-6 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Back to Accounts
            </button>
          </>
        )}
      </div>
    </div>
  );
}
